import { FC, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTherapists } from '../hooks/useTherapists';
import { Layout } from '../components/layout';
import { PublicTherapist } from '../types/booking.types';

/**
 * Star rating component
 */
const StarRating: FC<{ rating: number | null; totalReviews: number }> = ({
  rating,
  totalReviews,
}) => {
  if (rating === null) {
    return <span className="text-sm text-gray-500 dark:text-slate-400">No reviews yet</span>;
  }

  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${star <= Math.round(rating) ? 'text-yellow-400' : 'text-gray-300 dark:text-slate-600'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <span className="text-sm text-gray-600 dark:text-slate-400">
        {rating.toFixed(1)} ({totalReviews} review{totalReviews !== 1 ? 's' : ''})
      </span>
    </div>
  );
};

/**
 * Therapist card component
 */
const TherapistCard: FC<{ therapist: PublicTherapist }> = ({ therapist }) => {
  // Get initials from full name
  const nameParts = therapist.name.split(' ');
  const initials = nameParts.length >= 2
    ? `${nameParts[0]?.[0] || ''}${nameParts[1]?.[0] || ''}`
    : therapist.name.substring(0, 2);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden hover:shadow-md transition-shadow">
      {/* Image / Avatar */}
      <div className="aspect-[4/3] bg-gradient-to-br from-primary-100 to-primary-200 relative">
        {therapist.photoUrl ? (
          <img
            src={therapist.photoUrl}
            alt={therapist.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 bg-white dark:bg-slate-700 rounded-full flex items-center justify-center shadow-sm">
              <span className="text-3xl font-semibold text-primary-600">{initials}</span>
            </div>
          </div>
        )}

        {/* Status Badge */}
        {!therapist.isAvailable && (
          <div className="absolute top-3 right-3 bg-gray-900/70 text-white text-xs px-2 py-1 rounded-full">
            Currently unavailable
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">{therapist.name}</h3>

        {/* Rating */}
        <div className="mb-3">
          <StarRating rating={therapist.rating} totalReviews={therapist.totalReviews} />
        </div>

        {/* Bio */}
        {therapist.bio && (
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-4 line-clamp-2">{therapist.bio}</p>
        )}

        {/* Specializations */}
        {therapist.specializations.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {therapist.specializations.slice(0, 3).map((spec, index) => (
              <span
                key={index}
                className="text-xs bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-full"
              >
                {spec}
              </span>
            ))}
            {therapist.specializations.length > 3 && (
              <span className="text-xs text-gray-500 dark:text-slate-400 px-2 py-1">
                +{therapist.specializations.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Link
            to={`/booking?therapistId=${therapist.id}`}
            className={`flex-1 text-center px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              therapist.isAvailable
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
            }`}
            onClick={(e) => !therapist.isAvailable && e.preventDefault()}
          >
            Book Now
          </Link>
          <Link
            to={`/therapists/${therapist.id}`}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg font-medium text-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            Profile
          </Link>
        </div>
      </div>
    </div>
  );
};

/**
 * Filter button component
 */
const FilterButton: FC<{
  label: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary-600 text-white'
        : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700'
    }`}
  >
    {label}
  </button>
);

/**
 * TherapistsPage - Public page showing all therapists
 */
export const TherapistsPage: FC = () => {
  const { data: therapists, isLoading, error } = useTherapists();
  const [selectedSpecialization, setSelectedSpecialization] = useState<string | null>(null);

  // Extract unique specializations from all therapists
  const specializations = useMemo(() => {
    if (!therapists) return [];
    const allSpecs = therapists.flatMap((t) => t.specializations);
    return [...new Set(allSpecs)].sort();
  }, [therapists]);

  // Filter therapists by selected specialization
  const filteredTherapists = useMemo(() => {
    if (!therapists) return [];
    if (!selectedSpecialization) return therapists;
    return therapists.filter((t) => t.specializations.includes(selectedSpecialization));
  }, [therapists, selectedSpecialization]);

  return (
    <Layout>
      <Helmet>
        <title>Our Therapists | Massatherapy</title>
        <meta
          name="description"
          content="Meet our team of licensed massage therapists. View profiles, specializations, and book your next session with a qualified professional."
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-slate-100 mb-4">Our Therapists</h1>
            <p className="text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
              Meet our team of licensed and certified massage therapists. Each professional brings
              unique expertise to help you achieve your wellness goals.
            </p>
          </div>

          {/* Filters */}
          {specializations.length > 0 && (
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-3 justify-center">
                <span className="text-sm text-gray-600 dark:text-slate-400 font-medium">Filter by specialty:</span>
                <FilterButton
                  label="All"
                  isActive={selectedSpecialization === null}
                  onClick={() => setSelectedSpecialization(null)}
                />
                {specializations.map((spec) => (
                  <FilterButton
                    key={spec}
                    label={spec}
                    isActive={selectedSpecialization === spec}
                    onClick={() => setSelectedSpecialization(spec)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-slate-400">Loading therapists...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="max-w-md mx-auto p-8 bg-white dark:bg-slate-800 rounded-lg shadow text-center">
              <div className="text-red-500 text-5xl mb-4">!</div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-2">Unable to Load Therapists</h2>
              <p className="text-gray-600 dark:text-slate-400 mb-6">
                {error instanceof Error ? error.message : 'An unexpected error occurred'}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Therapists Grid */}
          {!isLoading && !error && filteredTherapists && (
            <>
              {filteredTherapists.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTherapists.map((therapist) => (
                    <TherapistCard key={therapist.id} therapist={therapist} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
                  <svg
                    className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <p className="text-gray-600 dark:text-slate-400 mb-4">
                    {selectedSpecialization
                      ? `No therapists found with "${selectedSpecialization}" specialization.`
                      : 'No therapists available at the moment.'}
                  </p>
                  {selectedSpecialization && (
                    <button
                      onClick={() => setSelectedSpecialization(null)}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* CTA Section */}
          {!isLoading && !error && filteredTherapists && filteredTherapists.length > 0 && (
            <div className="mt-16 text-center">
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-2xl p-8 md:p-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-4">
                  Ready to Book Your Session?
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-6 max-w-xl mx-auto">
                  Browse our services and find the perfect massage therapy for your needs. Our
                  therapists are ready to help you feel your best.
                </p>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
                >
                  View Services
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

TherapistsPage.displayName = 'TherapistsPage';

export default TherapistsPage;
